/**
 * 無料特典自動追加 (free-gift.js) — 交換方式・複数ケース・商品タグ判定版
 *
 * 各ケース: 対象タグ付き商品の合計（税込・割引後）が条件金額以上で、かつ
 * カートに「交換元商品（単品）」が入っているとき、単品を1個減らして
 * 特典商品（サイン入り等・¥0）を1個追加する（＝1個がサイン入り無料版に変わる）。
 *
 * ルール:
 * - 特典はケースごとに常に1枚まで（何個買っても・いくら超えても1枚）
 * - 単品がカートに無ければ何もしない（交換のみ・特典単独追加はしない）
 * - 条件を下回ったら特典を削除し、減らした単品を1個戻す
 * - お客様が特典を手動削除しても、条件達成中は特典だけ復活（単品は再度減らさない）
 *
 * 仕組み:
 * - 交換実行時に cart attributes に「_fg_swap_{ケース番号} = 減らした1個分の金額(セント)」を記録
 *   → 判定合計に常にこの金額を足し戻すことで、交換による金額低下→特典消滅のループを防ぐ
 *   → 特典が消えていても交換済みかどうか判別でき、二重に−1しない
 * - 特典line itemには property「_free_gift: true」「_free_gift_case: 番号」を付与
 * - 商品タグ・バリアントIDは /products/{handle}.js から取得（IDハードコードなし）
 * - 特典の在庫切れ・取得失敗時は何もしない（エラー非表示）
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('free-gift-config');
  if (!cfgEl) return;

  var campaigns;
  try { campaigns = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  campaigns = (campaigns || []).filter(function (c) {
    return c && c.enabled && c.tag && c.handle && c.swapHandle && c.threshold > 0;
  });
  campaigns.forEach(function (c) { c.thresholdCents = c.threshold * 100; });
  if (!campaigns.length) return;

  var GIFT_PROP = '_free_gift';
  var CASE_PROP = '_free_gift_case';
  var ATTR_PREFIX = '_fg_swap_';
  var busy = false;
  var queued = false;
  var evaluateTimer = null;
  var tagCache = {}; // handle -> tags配列

  /* ---------- API helpers ---------- */

  function getCart() {
    return fetch('/cart.js', { cache: 'no-store' }).then(function (r) {
      return r.ok ? r.json() : null;
    });
  }

  function getProductJson(handle, noStore) {
    var opts = noStore ? { cache: 'no-store' } : {};
    return fetch('/products/' + encodeURIComponent(handle) + '.js', opts)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function getTags(handle) {
    if (tagCache[handle]) return Promise.resolve(tagCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) {
        if (r.status === 404) return { tags: [] }; // 未公開/削除商品はタグなし扱い（判定は継続）
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        if (!p || !p.tags) return null; // 通信失敗時のみ今回の判定を中止（誤操作防止）
        tagCache[handle] = p.tags;
        return p.tags;
      })
      .catch(function () { return null; });
  }

  function getFirstVariant(handle) {
    return getProductJson(handle, true).then(function (p) {
      if (!p || !p.variants || !p.variants.length) return null;
      var v = p.variants[0];
      return { id: v.id, available: !!v.available, price: v.price };
    });
  }

  function addItem(variantId, quantity, props) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity, properties: props || {} }] })
    }); // 失敗（在庫切れ422等）は握りつぶす
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  function setAttr(name, value) {
    var attrs = {};
    attrs[name] = value; // '' で実質クリア
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  /* ---------- 判定ロジック ---------- */

  function isGiftLine(item) {
    return !!(item.properties && item.properties[GIFT_PROP]);
  }

  function attrAmount(cart, caseId) {
    var v = cart.attributes && cart.attributes[ATTR_PREFIX + caseId];
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  function evaluate() {
    if (busy) { queued = true; return; }
    busy = true;
    var mutated = false;

    getCart().then(function (cart) {
      if (!cart) return finish();

      var giftLines = cart.items.filter(isGiftLine);
      var normalLines = cart.items.filter(function (i) { return !isGiftLine(i); });

      var handles = [];
      normalLines.forEach(function (i) {
        if (handles.indexOf(i.handle) === -1) handles.push(i.handle);
      });

      return Promise.all(handles.map(getTags)).then(function (tagLists) {
        // タグ取得に1件でも失敗したら今回は判定しない（誤操作防止）
        if (tagLists.some(function (t) { return t === null; })) return finish();
        var tagsByHandle = {};
        handles.forEach(function (h, idx) { tagsByHandle[h] = tagLists[idx]; });

        var knownCaseIds = [];
        var caseStates = {};
        var ops = Promise.resolve();

        // 特典は1注文1つまで: いずれかのケースで交換済み（特典 or 交換記録あり）なら
        // 他ケースは新規交換を行わない。ケース番号の小さい方が優先。
        var orderHasBenefit = giftLines.length > 0;
        campaigns.forEach(function (c) {
          if (attrAmount(cart, c.id) > 0) orderHasBenefit = true;
        });
        var newGiftPlanned = false;

        campaigns.forEach(function (c) {
          knownCaseIds.push(String(c.id));

          var taggedTotal = normalLines.reduce(function (s, i) {
            return (tagsByHandle[i.handle] || []).indexOf(c.tag) !== -1 ? s + i.final_line_price : s;
          }, 0);
          var swapped = attrAmount(cart, c.id); // 交換で減らした1個分を足し戻す
          var eligibleTotal = taggedTotal + swapped;
          var hasTaggedItem = normalLines.some(function (i) {
            return (tagsByHandle[i.handle] || []).indexOf(c.tag) !== -1;
          }) || swapped > 0;

          var qualifies = hasTaggedItem && eligibleTotal >= c.thresholdCents;
          var myGifts = giftLines.filter(function (i) {
            return i.properties[CASE_PROP] === String(c.id);
          });
          var swapLine = normalLines.filter(function (i) { return i.handle === c.swapHandle; })[0];

          caseStates[c.id] = { total: eligibleTotal, gift: myGifts.length > 0, swapInCart: !!swapLine };

          if (!qualifies) {
            // 条件未達 → 特典を削除し、交換済みなら単品を1個戻す
            myGifts.forEach(function (line) {
              ops = ops.then(function () { mutated = true; return changeLine(line.key, 0); });
            });
            if (swapped > 0) {
              if (normalLines.length > 0) {
                // 交換で減らした単品を1個戻す
                ops = ops.then(function () {
                  mutated = true;
                  return getFirstVariant(c.swapHandle).then(function (v) {
                    var restore = swapLine
                      ? changeLine(swapLine.key, swapLine.quantity + 1)
                      : (v ? addItem(v.id, 1) : Promise.resolve());
                    return restore.then(function () { return setAttr(ATTR_PREFIX + c.id, ''); });
                  });
                });
              } else {
                // カートが空にされた場合は復元せず記録だけクリア
                ops = ops.then(function () { return setAttr(ATTR_PREFIX + c.id, ''); });
              }
            }
            return;
          }

          // ---- 条件達成 ----
          if (myGifts.length === 0) {
            if (swapped > 0) {
              // 交換済みだが特典が無い（お客様が手動削除）→ 特典だけ復活（−1はしない）
              ops = ops.then(function () {
                return getFirstVariant(c.handle).then(function (gv) {
                  if (!gv || !gv.available) return;
                  var props = {};
                  props[GIFT_PROP] = 'true';
                  props[CASE_PROP] = String(c.id);
                  return addItem(gv.id, 1, props).then(function (r) { if (r && r.ok) mutated = true; });
                });
              });
            } else if (swapLine && !orderHasBenefit && !newGiftPlanned) {
              // 交換実行: 単品−1 → attribute記録 → 特典+1（1注文1つまで）
              newGiftPlanned = true;
              var unitPrice = swapLine.final_price; // 割引後の単価
              var newQty = swapLine.quantity - 1;
              var lineKey = swapLine.key;
              ops = ops.then(function () {
                return getFirstVariant(c.handle).then(function (gv) {
                  if (!gv || !gv.available) return; // 特典在庫なし→交換しない
                  mutated = true;
                  return changeLine(lineKey, newQty)
                    .then(function () { return setAttr(ATTR_PREFIX + c.id, String(unitPrice)); })
                    .then(function () {
                      var props = {};
                      props[GIFT_PROP] = 'true';
                      props[CASE_PROP] = String(c.id);
                      return addItem(gv.id, 1, props);
                    });
                });
              });
            }
            // 単品がカートに無い場合は何もしない（交換のみ）
            return;
          }

          // 重複防止: 特典は常に1行・数量1
          myGifts.slice(1).forEach(function (line) {
            ops = ops.then(function () { mutated = true; return changeLine(line.key, 0); });
          });
          if (myGifts[0].quantity !== 1) {
            var key0 = myGifts[0].key;
            ops = ops.then(function () { mutated = true; return changeLine(key0, 1); });
          }
        });

        // どのケースにも属さない特典行（設定変更で孤児化）を削除
        giftLines.forEach(function (line) {
          if (knownCaseIds.indexOf(line.properties[CASE_PROP]) === -1) {
            ops = ops.then(function () { mutated = true; return changeLine(line.key, 0); });
          }
        });

        updateProgressBars(caseStates);
        return ops.then(finish);
      });
    }).catch(function () { finish(); });

    function finish() {
      busy = false;
      if (mutated) refreshCartUI();
      if (queued) { queued = false; scheduleEvaluate(); }
    }
  }

  function scheduleEvaluate() {
    clearTimeout(evaluateTimer);
    evaluateTimer = setTimeout(evaluate, 350);
  }

  /* ---------- UI 更新 ---------- */

  function formatYen(cents) {
    return Math.ceil(cents / 100).toLocaleString('ja-JP') + '円';
  }

  function updateProgressBars(caseStates) {
    // 注文全体で特典を獲得済みか（1注文1特典のため、獲得ケース以外のバーは隠す）
    var orderHasGift = campaigns.some(function (c) {
      return caseStates[c.id] && caseStates[c.id].gift;
    });
    campaigns.forEach(function (c, idx) {
      var st = caseStates[c.id] || { total: 0, gift: false, swapInCart: false };
      var els = document.querySelectorAll('[data-free-gift-progress][data-fg-case="' + c.id + '"]');
      if (!els.length) return;
      var remaining = Math.max(0, c.thresholdCents - st.total);
      var pct = Math.min(100, Math.round((st.total / c.thresholdCents) * 100));
      // 同一条件（タグ・金額）の先行ケースがある場合、未達バーは重複するため隠す
      var dupOfEarlier = campaigns.slice(0, idx).some(function (e) {
        return e.tag === c.tag && e.thresholdCents === c.thresholdCents;
      });
      els.forEach(function (el) {
        el.hidden = st.total <= 0
          || (orderHasGift && !st.gift)
          || (!orderHasGift && remaining > 0 && dupOfEarlier);
        var bar = el.querySelector('[data-fg-bar]');
        var msg = el.querySelector('[data-fg-message]');
        if (bar) bar.style.width = pct + '%';
        if (msg) {
          if (remaining > 0) {
            msg.textContent = 'あと' + formatYen(remaining) + 'で、特典が受け取れます！';
          } else if (st.gift) {
            msg.textContent = '🎉 「' + (c.giftTitle || '特典版') + '」と交換しました';
          } else {
            msg.textContent = '条件達成！「' + (c.swapTitle || '対象商品') + '」をカートに追加すると「' + (c.giftTitle || '特典版') + '」と交換されます';
          }
        }
        el.classList.toggle('is-achieved', remaining <= 0);
      });
    });
  }

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('free-gift:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (typeof window.FreeGiftRefreshHook === 'function') {
      try { window.FreeGiftRefreshHook(); return; } catch (e) { /* noop */ }
    }
    if (/^\/(cart|checkouts?)\b/.test(location.pathname)) {
      location.reload();
    }
  }

  /* ---------- カート更新の検知 ---------- */

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?([?#]|$)/;

  var origFetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    var result = origFetch.apply(this, arguments);
    if (CART_MUTATION_RE.test(url)) {
      result.then(function () { scheduleEvaluate(); }, function () {});
    }
    return result;
  };

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (url && CART_MUTATION_RE.test(url)) {
      this.addEventListener('loadend', scheduleEvaluate);
    }
    return origOpen.apply(this, arguments);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleEvaluate);
  } else {
    scheduleEvaluate();
  }
})();
